import React, { useState } from 'react';
import { Modal, ModalOverlay, ModalContent, ModalBody } from '@chakra-ui/react';
import { MdClose, MdAdd, MdDelete, MdLightbulb, MdAutorenew } from 'react-icons/md';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import { Objective, KPI, Priority } from '../types';

// ── Auto-suggestion by objective name keyword ─────────────────────────────────

const SUGGESTIONS: Record<string, string[]> = {
    réclamation: ['Nombre de réclamations', 'Temps de traitement (h)', 'Satisfaction SAV (%)'],
    'chiffre d': ["CA mensuel (€)", 'Nouveaux clients/mois', 'Panier moyen (€)'],
    rupture: ['Taux de disponibilité (%)', 'Ruptures/mois', 'Délai réappro (j)'],
    'non-conformit': ['Nombre NC/mois', 'Coût NC (€)', 'Taux de conformité (%)'],
    livraison: ['Livraisons à temps (%)', 'NPS client', 'Délai moyen (j)'],
    stock: ['Taux de rotation (%)', 'Valeur stock (€)', 'Références en rupture'],
};

const findSuggestion = (name: string): string[] | null => {
    const lower = name.toLowerCase();
    const match = Object.entries(SUGGESTIONS).find(([k]) => lower.includes(k));
    return match ? match[1] : null;
};

// ── Sub-components ────────────────────────────────────────────────────────────

interface NewKPI {
    name: string;
    currentValue: string;
    targetValue: string;
    unit: string;
    direction: 'higher-is-better' | 'lower-is-better';
    auto_sync: boolean;
    module: string;
    metric_key: string;
}

const emptyKPI = (): NewKPI => ({
    name: '', currentValue: '', targetValue: '', unit: '',
    direction: 'higher-is-better', auto_sync: false, module: '', metric_key: '',
});

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (obj: Objective, kpis: KPI[]) => void;
    teamMembers: { id: string; name: string }[];
    availableMetrics?: { module: string; metric_key: string; label: string }[];
    // Edit mode
    editObjective?: Objective;
    onUpdate?: (id: string, data: Record<string, any>) => void;
}

const CreateObjectiveModal: React.FC<Props> = ({
    isOpen, onClose, onSubmit, teamMembers, availableMetrics = [],
    editObjective, onUpdate,
}) => {
    const isEdit = !!editObjective;
    const [step, setStep] = useState<1 | 2>(1);
    const [name, setName] = useState(editObjective?.name ?? '');
    const [description, setDescription] = useState(editObjective?.description ?? '');
    const [category, setCategory] = useState(editObjective?.category ?? '');
    const [responsibleUserId, setResponsibleUserId] = useState(editObjective?.responsibleUserId ?? editObjective?.ownerId ?? '');
    const [startDate, setStartDate] = useState(editObjective?.startDate?.split('T')[0] ?? '');
    const [endDate, setEndDate] = useState(editObjective?.endDate?.split('T')[0] ?? '');
    const [priority, setPriority] = useState<Priority>(editObjective?.priority ?? 'high');
    const [status, setStatus] = useState(editObjective?.status ?? 'not-started');
    const [kpis, setKpis] = useState<NewKPI[]>([emptyKPI()]);

    // Sync fields when editObjective changes
    React.useEffect(() => {
        if (editObjective) {
            setName(editObjective.name);
            setDescription(editObjective.description ?? '');
            setCategory(editObjective.category ?? '');
            setResponsibleUserId(editObjective.responsibleUserId ?? editObjective.ownerId ?? '');
            setStartDate(editObjective.startDate?.split('T')[0] ?? '');
            setEndDate(editObjective.endDate?.split('T')[0] ?? '');
            setPriority(editObjective.priority);
            setStatus(editObjective.status);
        }
    }, [editObjective]);

    const suggestion = isEdit ? null : findSuggestion(name);

    const reset = () => {
        setStep(1); setName(''); setDescription(''); setCategory('');
        setResponsibleUserId(''); setStartDate(''); setEndDate('');
        setPriority('high'); setStatus('not-started'); setKpis([emptyKPI()]);
    };
    const handleClose = () => { if (!isEdit) reset(); onClose(); };

    const handleSubmit = () => {
        if (!name.trim() || !responsibleUserId || !startDate || !endDate) return;

        if (isEdit && onUpdate && editObjective) {
            onUpdate(editObjective.id || editObjective._id || '', {
                name: name.trim(), description: description.trim(),
                category: category.trim() || 'Général', responsibleUserId,
                startDate, endDate, priority, status,
            });
            onClose();
            return;
        }

        const objId = `obj-${Date.now()}`;
        const newKPIs: KPI[] = kpis.filter((k) => k.name.trim()).map((k, i) => ({
            id: `kpi-${Date.now()}-${i}`, objectiveId: objId,
            name: k.name.trim(),
            currentValue: parseFloat(k.currentValue) || 0,
            targetValue: parseFloat(k.targetValue) || 100,
            minAcceptableValue: 0,
            unit: k.unit.trim(),
            direction: k.direction, trend: 'stable' as const, history: [] as { date: string; value: number }[],
            auto_sync: k.auto_sync, module: k.module, metric_key: k.metric_key,
        }));
        const obj: Objective = {
            id: objId, name: name.trim(), description: description.trim(),
            category: category.trim() || 'Général', responsibleUserId,
            startDate, endDate, priority, status: 'not-started',
            progress_mode: 'auto', kpiIds: newKPIs.map((k) => k.id), actionPlanIds: [],
        };
        onSubmit(obj, newKPIs);
        reset(); onClose();
    };

    const updateKPI = (i: number, field: keyof NewKPI, value: string | boolean) =>
        setKpis((prev) => prev.map((k, idx) => idx === i ? { ...k, [field]: value } : k));

    const applySuggestion = (names: string[]) => {
        setKpis(names.map((n) => ({ ...emptyKPI(), name: n })));
        setStep(2);
    };

    const sLabel = 'text-sm font-medium text-navy-700 mb-2 block';
    const sInput = 'w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-toola-500 focus:ring-0';
    const priorities: { key: Priority; label: string }[] = [
        { key: 'critical', label: 'Critique' }, { key: 'high', label: 'Haute' },
        { key: 'medium', label: 'Moyenne' }, { key: 'low', label: 'Faible' },
    ];
    const pColors: Record<Priority, string> = {
        critical: 'border-red-400 bg-red-50 text-red-600',
        high: 'border-orange-400 bg-orange-50 text-orange-600',
        medium: 'border-amber-400 bg-amber-50 text-amber-600',
        low: 'border-gray-300 bg-gray-50 text-gray-500',
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="xl">
            <ModalOverlay className="bg-[#000] !opacity-50 backdrop-blur-sm" />
            <ModalContent className="md:top-[5vh] !overflow-hidden !z-[1002]">
                <ModalBody className="p-0">
                    <Card extra="p-6 max-h-[88vh] overflow-y-auto">

                        {/* Header */}
                        <div className="flex items-start justify-between mb-5 pb-4 border-b border-gray-100">
                            <div>
                                <h2 className="text-xl font-semibold text-navy-700">
                                    {isEdit ? 'Modifier l\'objectif' : step === 1 ? 'Nouvel objectif' : 'Associer des KPIs'}
                                </h2>
                                {!isEdit && <p className="text-sm text-gray-500 mt-0.5">Étape {step} / 2</p>}
                            </div>
                            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 mt-0.5">
                                <MdClose className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Step 1 — objective details */}
                        {step === 1 && (
                            <div className="space-y-5">
                                <InputField
                                    label="Nom de l'objectif *"
                                    placeholder="Ex : Réduire les réclamations clients"
                                    id="obj-name"
                                    type="text"
                                    value={name}
                                    onChange={(e: any) => setName(e.target.value)}
                                />

                                {/* Suggestion banner */}
                                {suggestion && name.length > 4 && (
                                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                                        <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5 mb-2">
                                            <MdLightbulb className="w-3.5 h-3.5" /> Suggestions automatiques
                                        </p>
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {suggestion.map((s) => (
                                                <span key={s} className="text-xs bg-white border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full">{s}</span>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => applySuggestion(suggestion)}
                                            className="text-xs font-semibold text-amber-700 hover:text-amber-800 underline"
                                        >
                                            Appliquer ces KPIs →
                                        </button>
                                    </div>
                                )}

                                <div>
                                    <label className={sLabel}>Description</label>
                                    <textarea
                                        rows={3}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Décrivez l'objectif et l'impact attendu…"
                                        className={`${sInput} resize-none`}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={sLabel}>Catégorie</label>
                                        <input
                                            type="text"
                                            value={category}
                                            onChange={(e) => setCategory(e.target.value)}
                                            placeholder="Qualité, Commercial…"
                                            className={sInput}
                                        />
                                    </div>
                                    <div>
                                        <label className={sLabel}>Responsable *</label>
                                        <select
                                            value={responsibleUserId}
                                            onChange={(e) => setResponsibleUserId(e.target.value)}
                                            className={sInput}
                                        >
                                            <option value="">Sélectionner…</option>
                                            {teamMembers.map((m) => (
                                                <option key={m.id} value={m.id}>{m.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <InputField
                                        label="Date de début *"
                                        id="obj-start"
                                        type="date"
                                        value={startDate}
                                        onChange={(e: any) => setStartDate(e.target.value)}
                                    />
                                    <InputField
                                        label="Date de fin *"
                                        id="obj-end"
                                        type="date"
                                        value={endDate}
                                        onChange={(e: any) => setEndDate(e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className={sLabel}>Priorité</label>
                                    <div className="flex gap-2">
                                        {priorities.map(({ key, label }) => (
                                            <button
                                                key={key}
                                                onClick={() => setPriority(key)}
                                                className={`flex-1 py-2 text-xs font-semibold rounded-xl border-2 transition-all ${
                                                    priority === key
                                                        ? pColors[key]
                                                        : 'border-gray-100 text-gray-400 hover:border-gray-200'
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Statut — only shown in edit mode */}
                                {isEdit && (
                                    <div>
                                        <label className={sLabel}>Statut</label>
                                        <select value={status} onChange={(e) => setStatus(e.target.value as any)} className={sInput}>
                                            <option value="not-started">Non démarré</option>
                                            <option value="on-track">En avance</option>
                                            <option value="at-risk">À risque</option>
                                            <option value="behind">En retard</option>
                                            <option value="completed">Terminé</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 2 — KPIs */}
                        {step === 2 && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-gray-600">KPIs associés à cet objectif</p>
                                    <button
                                        onClick={() => setKpis((p) => [...p, emptyKPI()])}
                                        className="flex items-center gap-1 text-xs text-toola-500 hover:text-toola-600 font-medium"
                                    >
                                        <MdAdd className="w-4 h-4" /> Ajouter un KPI
                                    </button>
                                </div>

                                {kpis.map((kpi, i) => (
                                    <div key={i} className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs font-medium text-gray-500">KPI {i + 1}</p>
                                            {kpis.length > 1 && (
                                                <button
                                                    onClick={() => setKpis((p) => p.filter((_, idx) => idx !== i))}
                                                    className="text-gray-300 hover:text-red-400 transition-colors"
                                                >
                                                    <MdDelete className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>

                                        <InputField
                                            label="Nom du KPI *"
                                            placeholder="Ex : Nombre de réclamations"
                                            id={`kpi-name-${i}`}
                                            type="text"
                                            value={kpi.name}
                                            onChange={(e: any) => updateKPI(i, 'name', e.target.value)}
                                        />

                                        <div className="grid grid-cols-3 gap-3">
                                            <InputField
                                                label="Valeur actuelle"
                                                id={`kpi-current-${i}`}
                                                type="number"
                                                placeholder="0"
                                                value={kpi.currentValue}
                                                onChange={(e: any) => updateKPI(i, 'currentValue', e.target.value)}
                                            />
                                            <InputField
                                                label="Cible"
                                                id={`kpi-target-${i}`}
                                                type="number"
                                                placeholder="100"
                                                value={kpi.targetValue}
                                                onChange={(e: any) => updateKPI(i, 'targetValue', e.target.value)}
                                            />
                                            <InputField
                                                label="Unité"
                                                id={`kpi-unit-${i}`}
                                                type="text"
                                                placeholder="%, €, jours…"
                                                value={kpi.unit}
                                                onChange={(e: any) => updateKPI(i, 'unit', e.target.value)}
                                            />
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => updateKPI(i, 'direction', 'higher-is-better')}
                                                className={`flex-1 text-xs py-2 rounded-xl border-2 font-medium transition-all ${
                                                    kpi.direction === 'higher-is-better'
                                                        ? 'border-green-400 bg-green-50 text-green-600'
                                                        : 'border-gray-100 text-gray-400 hover:border-gray-200'
                                                }`}
                                            >
                                                ↑ Plus haut = mieux
                                            </button>
                                            <button
                                                onClick={() => updateKPI(i, 'direction', 'lower-is-better')}
                                                className={`flex-1 text-xs py-2 rounded-xl border-2 font-medium transition-all ${
                                                    kpi.direction === 'lower-is-better'
                                                        ? 'border-toola-400 bg-blue-50 text-toola-500'
                                                        : 'border-gray-100 text-gray-400 hover:border-gray-200'
                                                }`}
                                            >
                                                ↓ Plus bas = mieux
                                            </button>
                                        </div>

                                        {/* MetricBridge auto-sync (shown only when metrics available) */}
                                        {availableMetrics.length > 0 && (
                                            <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={kpi.auto_sync}
                                                        onChange={(e) => updateKPI(i, 'auto_sync', e.target.checked)}
                                                        className="rounded border-gray-300 text-toola-500"
                                                    />
                                                    <span className="text-xs font-medium text-navy-700 flex items-center gap-1">
                                                        <MdAutorenew className="w-3.5 h-3.5 text-toola-400" />
                                                        Synchronisation automatique (MetricBridge)
                                                    </span>
                                                </label>
                                                {kpi.auto_sync && (
                                                    <select
                                                        className={sInput}
                                                        value={`${kpi.module}:${kpi.metric_key}`}
                                                        onChange={(e) => {
                                                            const [m, k] = e.target.value.split(':');
                                                            updateKPI(i, 'module', m);
                                                            updateKPI(i, 'metric_key', k);
                                                        }}
                                                    >
                                                        <option value=":">Sélectionner une métrique…</option>
                                                        {availableMetrics.map((m) => (
                                                            <option key={`${m.module}:${m.metric_key}`} value={`${m.module}:${m.metric_key}`}>
                                                                [{m.module}] {m.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Footer */}
                        <div className="mt-6 flex gap-3 pt-4 border-t border-gray-100">
                            {isEdit ? (
                                <>
                                    <button onClick={handleClose} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                                        Annuler
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={!name.trim() || !responsibleUserId || !startDate || !endDate}
                                        className="linear flex-1 rounded-xl bg-toola-500 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-toola-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        Enregistrer
                                    </button>
                                </>
                            ) : (
                                <>
                                    {step === 2 && (
                                        <button onClick={() => setStep(1)} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                                            ← Retour
                                        </button>
                                    )}
                                    {step === 1 ? (
                                        <button
                                            onClick={() => setStep(2)}
                                            disabled={!name.trim() || !responsibleUserId || !startDate || !endDate}
                                            className="linear flex-1 rounded-xl bg-toola-500 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-toola-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            Suivant : KPIs →
                                        </button>
                                    ) : (
                                        <button onClick={handleSubmit} className="linear flex-1 rounded-xl bg-toola-500 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-toola-600">
                                            Créer l'objectif
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default CreateObjectiveModal;
